// apps/frontend/src/components/onboarding/GlowpTutorialModal.tsx
//
// Tutorial Glowp : fenetre qui presente les fonctionnalites differenciantes
// en quelques slides. Toujours optionnelle, 3 actions de fin :
//  - "Plus tard"  : snooze 3 jours
//  - "Fermer"     : dismiss definitif
//  - "C'est parti" (sur derniere slide) : marque comme complete
//
// Ton chaud (memory feedback_human_tone). Pas de paternalisme : on dit
// "voici ce qui existe", on ne dit pas "tu dois apprendre tout ca".

import { useState } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Package,
  Layers,
  Palette,
  Brain,
  ShoppingBag,
  Users,
} from 'lucide-react';
import {
  markTutorialDismissed,
  markTutorialSnoozed,
  markTutorialCompleted,
} from '../../lib/tutorialState';

interface Slide {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
  body: string;
}

const SLIDES: Slide[] = [
  {
    icon: Sparkles,
    title: 'Bienvenue sur Glowp',
    body: 'Une marketplace mode pour toute la famille, tenue par des vendeuses humaines. On t’aide à acheter et vendre sans algorithme froid.',
  },
  {
    icon: Layers,
    title: 'Publie plusieurs pièces d’un coup',
    body: 'Mode lot : en bloc ou pièce par pièce. Tu vides un dressing en quelques minutes au lieu d’y passer la soirée.',
  },
  {
    icon: Package,
    title: 'Import par captures d’écran',
    body: 'Tu as déjà des photos sur une autre app ? Glisse-les, Glowp en devine N articles. Le formulaire se pré-remplit.',
  },
  {
    icon: Palette,
    title: 'Ta boutique, ton univers',
    body: 'Bannière, thème, collections nommées comme un lookbook. Et des remises sur lot configurables pour récompenser les acheteuses fidèles.',
  },
  {
    icon: Brain,
    title: 'Le coach Glowp t’accompagne',
    body: 'Suggestions de prix, score qualité photo, conseils sur le rythme de publication. Tu décides, on regarde ce qui peut bouger.',
  },
  {
    icon: ShoppingBag,
    title: 'Mission shopping',
    body: 'Donne ta liste, Glowp trouve les boutiques qui couvrent plusieurs articles. Un seul colis, plusieurs pièces.',
  },
  {
    icon: Users,
    title: 'Une communauté, pas une plateforme',
    body: 'Tu suis tes vendeuses préférées, tu sais quand elles publient, tu discutes les prix. Glowp parle humain, jamais SaaS.',
  },
];

interface GlowpTutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Mode demo : ne persiste rien, ne ferme pas via les actions
  // (juste via le bouton X). Permet de rejouer indefiniment.
  demoMode?: boolean;
}

export const GlowpTutorialModal: React.FC<GlowpTutorialModalProps> = ({
  isOpen,
  onClose,
  demoMode = false,
}) => {
  const [index, setIndex] = useState(0);

  if (!isOpen) return null;

  const slide = SLIDES[index];
  const Icon = slide.icon;
  const isFirst = index === 0;
  const isLast = index === SLIDES.length - 1;

  const handleNext = () => {
    if (isLast) {
      if (!demoMode) markTutorialCompleted();
      onClose();
      setIndex(0);
    } else {
      setIndex(index + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) setIndex(index - 1);
  };

  const handleSnooze = () => {
    if (!demoMode) markTutorialSnoozed();
    onClose();
    setIndex(0);
  };

  const handleDismiss = () => {
    if (!demoMode) markTutorialDismissed();
    onClose();
    setIndex(0);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-slide-up">
        {/* Header : croix de fermeture definitive */}
        <button
          onClick={handleDismiss}
          aria-label="Fermer"
          className="absolute top-4 right-4 z-10 p-2 rounded-full text-[#7d7060] hover:bg-[#faf8f4] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Visuel : grand cercle gradient avec icone */}
        <div className="pt-12 pb-2 flex flex-col items-center bg-gradient-to-b from-[#faf6ee] to-white">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C8A882] to-[#B89968] flex items-center justify-center shadow-lg shadow-[#C8A882]/20">
            <Icon className="w-9 h-9 text-white" strokeWidth={2} />
          </div>
        </div>

        {/* Texte */}
        <div className="px-7 pt-6 pb-2 text-center min-h-[140px]">
          <h2 className="text-[20px] font-semibold text-[#2d2a26] mb-2 leading-snug">
            {slide.title}
          </h2>
          <p className="text-[14px] text-[#7d7060] leading-relaxed">
            {slide.body}
          </p>
        </div>

        {/* Dots pagination */}
        <div className="flex justify-center gap-1.5 py-4">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index
                  ? 'w-6 bg-[#C8A882]'
                  : 'w-1.5 bg-[#e8e2d4] hover:bg-[#d4c4a8]'
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="px-6 pb-5 flex items-center gap-2">
          <button
            onClick={handlePrev}
            disabled={isFirst}
            aria-label="Précédent"
            className={`p-2.5 rounded-xl transition-colors ${
              isFirst
                ? 'text-[#d4c4a8] cursor-not-allowed'
                : 'text-[#2d2a26] hover:bg-[#faf8f4]'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            onClick={handleNext}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-3 rounded-2xl font-semibold text-white shadow-md transition-all bg-gradient-to-br from-[#C8A882] to-[#B89968] hover:shadow-lg active:scale-95"
          >
            {isLast ? "C'est parti" : 'Suivant'}
            {!isLast && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>

        {/* Pied : actions secondaires */}
        <div className="px-6 pb-5 flex items-center justify-center gap-4 text-[13px]">
          <button
            onClick={handleSnooze}
            className="text-[#7d7060] hover:text-[#2d2a26] transition-colors"
          >
            Plus tard
          </button>
          <span className="text-[#d4c4a8]">·</span>
          <button
            onClick={handleDismiss}
            className="text-[#7d7060] hover:text-[#2d2a26] transition-colors"
          >
            Ne plus afficher
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlowpTutorialModal;
